import { motion } from 'framer-motion';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import PriceCalculator from './components/PriceCalculator';
import { useInView } from '../home/hooks/useInView';

const TarifsPage = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 via-teal-500 to-teal-400 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-price-tag-3-line mr-2"></i>
              Tarifs transparents
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Nos <strong>Tarifs</strong>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
              Calculez instantanément le prix de votre vitrage avec notre outil en ligne. Devis gratuit et sans engagement.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:0554542545"
                className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                05 54 54 25 45
              </a>
              <a
                href="https://wa.me/33745883392"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-green-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl mr-2"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calculateur de prix */}
      <PriceCalculator />

      {/* Grille tarifaire */}
      <section ref={ref} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Grille <strong className="text-teal-600">Tarifaire</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nos tarifs sont transparents et compétitifs. Tous nos prix incluent la main-d'œuvre et le déplacement.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Double vitrage */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-lg p-8 border-2 border-teal-100 hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-window-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Double Vitrage</h3>
              <div className="text-4xl font-bold text-teal-600 mb-6">
                150€<span className="text-xl text-gray-600">/m²</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Isolation thermique optimale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Réduction des nuisances sonores</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Économies d'énergie</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Garantie 10 ans</span>
                </li>
              </ul>
            </motion.div>

            {/* Triple vitrage */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-teal-600 to-teal-500 rounded-2xl shadow-xl p-8 text-white transform scale-105 relative"
            >
              <div className="absolute -top-4 right-8 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Recommandé
              </div>
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6">
                <i className="ri-window-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Triple Vitrage</h3>
              <div className="text-4xl font-bold mb-6">
                220€<span className="text-xl text-white/80">/m²</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-white text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span>Isolation thermique maximale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-white text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span>Isolation phonique renforcée</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-white text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span>Économies d'énergie maximales</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-white text-xl mr-2 flex-shrink-0 mt-0.5"></i>
                  <span>Garantie 10 ans</span>
                </li>
              </ul>
            </motion.div>

            {/* Services supplémentaires */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg p-8 border-2 border-gray-100 hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-tools-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Services Inclus</h3>
              <ul className="space-y-4">
                <li className="flex items-start justify-between">
                  <span className="text-gray-700">Frais de déplacement</span>
                  <span className="font-bold text-gray-900">50€</span>
                </li>
                <li className="flex items-start justify-between">
                  <span className="text-gray-700">Frais de placement</span>
                  <span className="font-bold text-gray-900">90€</span>
                </li>
                <li className="flex items-start justify-between">
                  <span className="text-gray-700">Lift (si nécessaire)</span>
                  <span className="font-bold text-gray-900">175€</span>
                </li>
                <li className="flex items-start justify-between">
                  <span className="text-gray-700">Supplément étage (avec ascenseur)</span>
                  <span className="font-bold text-gray-900">30€/étage</span>
                </li>
                <li className="flex items-start justify-between">
                  <span className="text-gray-700">Supplément étage (sans ascenseur)</span>
                  <span className="font-bold text-gray-900">80€/étage</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nos garanties */}
      <section className="py-24 bg-gradient-to-b from-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nos <strong className="text-teal-600">Garanties</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Votre satisfaction est notre priorité. Nous nous engageons à vous offrir le meilleur service.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Garantie 10 ans</h3>
              <p className="text-gray-600">Sur tous nos vitrages et installations</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Devis gratuit</h3>
              <p className="text-gray-600">Sans engagement et détaillé</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-timer-flash-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Intervention rapide</h3>
              <p className="text-gray-600">Sous 2h en cas d'urgence</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-euro-circle-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Prix transparents</h3>
              <p className="text-gray-600">Aucune surprise sur la facture</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default TarifsPage;
